/**
 * Purchase Confirmation Dialog (§312j/§356 BGB)
 *
 * Shown before any paid purchase action (plan upgrade, credit pack).
 * Contains the withdrawal consent checkbox and a §312j-compliant CTA button.
 *
 * This ensures consent is collected per-transaction (LG Köln requirement)
 * instead of as a blanket page-level checkbox.
 */

import { ConfirmModal } from '@affine/component';
import { useI18n } from '@affine/i18n';
import { Fragment, useCallback, useRef, useState } from 'react';

import {
  type ConsentMode,
  type LegalConsentHandle,
  LegalConsentSection,
} from './legal-consent-section';
import * as styles from './legal-consent-section.css';
import type { PurchaseOrderSummary } from './purchase-order-summary';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PurchaseConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Display name of the product being purchased */
  productName: string;
  /** Formatted price string (e.g. "29,00 €/Monat") */
  priceLabel: string;
  /** Structured §312j order information shown immediately before purchase. */
  orderSummary?: PurchaseOrderSummary;
  /** Whether this is a subscription (recurring) or one-time purchase */
  isSubscription: boolean;
  /** Legal consent mode for the purchase. Defaults to service/Wertersatz. */
  consentMode?: ConsentMode;
  /** Loading state while checkout is being created */
  isLoading: boolean;
  /** Called when user confirms with consent given */
  onConfirm: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const PurchaseConfirmDialog = ({
  open,
  onOpenChange,
  productName,
  priceLabel,
  orderSummary,
  isSubscription,
  consentMode = 'service',
  isLoading,
  onConfirm,
}: PurchaseConfirmDialogProps) => {
  const t = useI18n();
  const [consentGiven, setConsentGiven] = useState(false);
  const consentRef = useRef<LegalConsentHandle>(null);

  const handleConfirm = useCallback(() => {
    if (!consentGiven) {
      consentRef.current?.scrollAndShake();
      return;
    }
    onConfirm();
  }, [consentGiven, onConfirm]);

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen) {
        setConsentGiven(false);
      }
      onOpenChange(newOpen);
    },
    [onOpenChange]
  );

  const confirmText = isSubscription
    ? t['com.streamlain.payment.pricing.subscribe-paid']()
    : t['com.streamlain.payment.confirm.buy-paid']();

  return (
    <ConfirmModal
      open={open}
      onOpenChange={handleOpenChange}
      title={t['com.streamlain.payment.confirm.title']()}
      confirmText={confirmText}
      confirmButtonOptions={{
        variant: 'primary',
        loading: isLoading,
        disabled: isLoading,
      }}
      onConfirm={handleConfirm}
      cancelText={t['com.streamlain.payment.confirm.cancel']()}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {orderSummary ? (
          <div className={styles.orderSummary}>
            <div className={styles.orderSummaryTitle}>
              {orderSummary.productName}
            </div>
            <div className={styles.orderSummaryRows}>
              {orderSummary.items.map(item => (
                <Fragment key={`${item.label}:${item.value}`}>
                  <div className={styles.orderSummaryLabel}>{item.label}</div>
                  <div
                    className={`${styles.orderSummaryValue} ${
                      item.emphasized ? styles.orderSummaryValueEmphasized : ''
                    }`}
                  >
                    {item.value}
                  </div>
                </Fragment>
              ))}
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ fontWeight: 600 }}>{productName}</div>
            <div
              style={{
                color: 'var(--streamlain-text-secondary-color, #6b7280)',
                whiteSpace: 'pre-line',
              }}
            >
              {priceLabel}
            </div>
          </div>
        )}

        <LegalConsentSection
          ref={consentRef}
          variant="purchase"
          consentMode={consentMode}
          consentGiven={consentGiven}
          onConsentChange={setConsentGiven}
        />
      </div>
    </ConfirmModal>
  );
};
